import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@interstate/components/Button';
import { Grid } from '@interstate/components/Grid';
import { ChevronRightIcon } from '@interstate/components/Icons/ChevronRightIcon';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { Typography } from '@interstate/components/Typography';

import { clearCatalogVehicles, fetchCatalogVehicles } from '../../store/actionCreators';
import { changeVehicleSelectors } from '../../store/config';
import BuildVehicleSearchResults from './BuildVehicleSearchResults';

import { createBuildVehicleSearchEvent } from '../../analytics';

import type { BuildVehicleInitValue, InventoryType } from './types';
import { BuildVehicleSelects, inventoryTypeOptions, inventoryTypeToVehicleConditionMap, selectLabels, selectOrder } from './types';
import useBuildVehicleSelectValues from './useBuildVehicleSelectValues';
import { clearNextSteps } from './utils';

type ValueOf<T> = T[keyof T];
type Entries<T> = [keyof T, ValueOf<T>][];

export type BuildVehicleProps = {
    initValue: BuildVehicleInitValue;
};

export const defaultDropdownHeight = 120;

const BuildVehicle = ({ initValue }: BuildVehicleProps) => {
    const dispatch = useDispatch();
    const [maxDropdownHeight, setMaxDropdownHeight] = useState(defaultDropdownHeight);
    const { vin, setVin, setIsInitialized, selectValues, setSelectValues } = useBuildVehicleSelectValues(initValue);

    const yearOptions = useSelector(changeVehicleSelectors.getCatalogYears);
    const makeOptions = useSelector(changeVehicleSelectors.getCatalogMakes);
    const modelOptions = useSelector(changeVehicleSelectors.getCatalogModels);
    const trimOptions = useSelector(changeVehicleSelectors.getCatalogTrims);
    const catalogVehicles = useSelector(changeVehicleSelectors.getCatalogVehicles);
    const isFetching = useSelector(changeVehicleSelectors.getIsFetching);
    const bffEndpoint = useSelector(changeVehicleSelectors.additionalSelectors.getServicesBff);

    const mappedCatalogVehicles = useMemo(() => {
        if (!selectValues.inventoryType) return catalogVehicles;
        // type and condition default to Used after mapping vehicle service response so they need to be overridden to selected inventory type
        const condition = inventoryTypeToVehicleConditionMap[selectValues.inventoryType as InventoryType];

        return catalogVehicles.map((vehicle) => ({
            ...vehicle,
            vin,
            condition,
            type: condition
        }));
    }, [catalogVehicles, vin, selectValues.inventoryType]);

    const selectOptions = useMemo(
        () => ({
            [BuildVehicleSelects.inventoryType]: inventoryTypeOptions,
            [BuildVehicleSelects.year]: yearOptions,
            [BuildVehicleSelects.make]: makeOptions,
            [BuildVehicleSelects.model]: modelOptions,
            [BuildVehicleSelects.trim]: trimOptions
        }),
        [yearOptions, makeOptions, modelOptions, trimOptions]
    );

    const isCreateButtonDisabled = !selectValues.inventoryType || !selectValues.trim;
    const showSearchResults = !isCreateButtonDisabled && mappedCatalogVehicles.length > 0;

    // Helper: enable dropdown only if all previous dropdowns are filled
    const isDropdownEnabled = (dropdownName: BuildVehicleSelects): boolean => {
        const index = selectOrder.indexOf(dropdownName);
        return selectOrder.slice(0, index).every((key) => selectValues[key]);
    };

    const handleSelectChange = (selectName: BuildVehicleSelects, value: SelectInputEventValue) => {
        const isChanged = selectValues[selectName] !== value;
        if (!isChanged) {
            return;
        }

        dispatch(clearCatalogVehicles());

        // istanbul ignore next: SelectInputEventValue allows value to be undefined
        const updatedValues = { ...selectValues, [selectName]: value || '' };

        if (selectName !== BuildVehicleSelects.inventoryType) {
            clearNextSteps(selectName, updatedValues);
            setVin(undefined);
            setIsInitialized(true);
        }

        setSelectValues(updatedValues);
    };

    const searchVehicles = () => {
        // Dispatch analytics event for Build Vehicle Search (vs-006)
        const buildOptions = {
            year: selectValues.year,
            make: selectValues.make,
            model: selectValues.model,
            trim: selectValues.trim,
            inventoryType: selectValues.inventoryType
        };
        const analyticsEvent = createBuildVehicleSearchEvent(buildOptions);
        dispatch(analyticsEvent);

        dispatch(fetchCatalogVehicles(bffEndpoint, selectValues.year, selectValues.make, selectValues.model, selectValues.trim));
    };

    /**
     * Calculates a free space above and below input element.
     * Sets a new height for the dropdown list to fit a free space.
     * Prefers a space below the input.
     */
    const handleDropdownClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
        const dropdownPadding = 8;
        const inputLabelHeight = 24;
        const enoughDropdownHeight = 175;

        const selectInputGroup = event.currentTarget;
        const container = selectInputGroup.offsetParent as HTMLDivElement;

        let dropdownHeight = defaultDropdownHeight;

        if (selectInputGroup && container) {
            const inputOffsetTop = selectInputGroup.offsetTop + inputLabelHeight;
            const inputOffsetBottom = container.offsetHeight - selectInputGroup.offsetTop - selectInputGroup.offsetHeight;

            const isEnoughHeightToOpenDownwards = inputOffsetBottom - dropdownPadding >= enoughDropdownHeight;
            const dropdownOffset = isEnoughHeightToOpenDownwards ? inputOffsetBottom : Math.max(inputOffsetTop, inputOffsetBottom);
            const calculatedDropdownHeight = dropdownOffset - dropdownPadding;

            dropdownHeight = calculatedDropdownHeight > defaultDropdownHeight ? calculatedDropdownHeight : defaultDropdownHeight;
        }

        setMaxDropdownHeight(dropdownHeight);
    }, []);

    useEffect(() => {
        if (initValue.year) {
            const inventoryTypeSelect = document.querySelector('#select-input-options-inventoryType') as HTMLElement;
            inventoryTypeSelect.click();
        }
    }, [initValue]);

    useEffect(() => {
        dispatch(clearCatalogVehicles());
    }, [dispatch]);

    return (
        <>
            <Typography variant="h4" sx={{ marginBottom: '16px' }}>
                Enter vehicle information
            </Typography>
            <Grid spacing={1} container xs rowGap={1} data-testid="main-finance-content" flexWrap="wrap">
                {(Object.entries(selectLabels) as Entries<typeof selectLabels>).map(([name, label]) => {
                    return (
                        <Grid xs={4} alignSelf="flex-end" key={name} onClick={handleDropdownClick}>
                            <SelectInput
                                maxHeight={`${maxDropdownHeight}px`}
                                label={label}
                                name={name}
                                onChange={(e) => handleSelectChange(name, e.target.value)}
                                options={
                                    selectValues[name] && !selectOptions[name].length
                                        ? [{ value: selectValues[name], label: selectValues[name] }]
                                        : selectOptions[name].map((value: string) => ({
                                              value,
                                              label: value
                                          }))
                                }
                                displayDeselectOption={false}
                                disabled={name !== BuildVehicleSelects.inventoryType && (isFetching || !isDropdownEnabled(name))}
                                value={selectValues[name]}
                                data-testid={name}
                                id={name}
                            />
                        </Grid>
                    );
                })}
                <Grid xs={4} sm={4} lg={0.8} alignSelf="flex-end">
                    <Button
                        buttonStyle="primary"
                        icon={<ChevronRightIcon />}
                        id="build-vehicle-button"
                        data-testid="build-vehicle-button"
                        size="medium"
                        disabled={isCreateButtonDisabled}
                        onClick={searchVehicles}
                    />
                </Grid>
            </Grid>
            {showSearchResults && <BuildVehicleSearchResults vehicles={mappedCatalogVehicles} selectValues={selectValues} />}
        </>
    );
};

export default BuildVehicle;
